import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { PurchaseStatusTemplatePending } from '@/components/v2/page-template-pending';
import { useQuery } from '@tanstack/react-query';
import { authenticatedGetServer } from '@/lib/server-fns/authenticated-get-fns';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { PageHeader } from '@/components/v2/page-header';
import { BackButton } from '@/components/v2/page-header/page-header-buttons';
import { usePurchase, useRetryPayment } from '@/hooks/data/use-market';
import { marketApi } from '@/lib/api/v2/market';
import { cacheFirst } from '@/lib/queries/cache-first';
import { goToCheckout, clearCheckoutIdempotencyKey, canRetryPayment } from '@/lib/utils/market';
import { useSmartBack } from '@/lib/navigation/smartBack';
import {
  PurchaseDetailError,
  PurchaseDetailLoading,
} from '@/components/v2/market/purchase-detail-states';
import type { PaymentStatus, PurchaseDetail, PurchaseStatusResponse } from '@/lib/api/v2/market';
import styles from '@/components/v2/market/market.module.less';

/**
 * ONE query factory for ['market', 'purchase', id, 'status'], shared by the
 * loader's cache seed and the component's polling query, so the seeded entry
 * and the polls can never drift in shape (perf audit part 2, Theme 1). The
 * environment branch mirrors authenticated-shared: HitPay returns land as
 * fresh documents where SSR must seed through the cookie-forwarding server
 * fn, while client refetches/polls call the Go API directly (one hop).
 *
 * Exported so the purchases list can data-prefetch it (bounded, kind
 * 'purchase') before the tap — the HitPay-return page this route serves is
 * the retry-payment destination, so warming status ahead of that flow shaves
 * its first poll.
 */
export const purchaseStatusQuery = (purchaseId: string) => ({
  queryKey: ['market', 'purchase', purchaseId, 'status'],
  queryFn: async (): Promise<PurchaseStatusResponse> => {
    if (typeof window === 'undefined') {
      return authenticatedGetServer({
        data: `/v1/market/purchases/${purchaseId}/status`,
      }) as Promise<PurchaseStatusResponse>;
    }
    const r = await marketApi.getStatus(purchaseId);
    if (!r.ok) throw new Error(r.error.message);
    return r.value;
  },
});

function PurchasePage() {
  const { purchaseId } = Route.useParams();
  // Back pops history (the user usually arrived from market/checkout);
  // cold-opened HitPay returns have nothing behind them, so they replace
  // to home instead of pushing a duplicate stack entry.
  const smartBackToHome = useSmartBack({ to: '/home' });
  const navigate = useNavigate();
  const { data: purchase, isError, refetch } = usePurchase(purchaseId);
  const retry = useRetryPayment();

  // Poll status while payment is awaiting; stops once resolved. Server-side GET-reconcile
  // fires after a short grace if the webhook hasn't arrived.
  //
  // Back off as the wait drags on: every poll can trigger that reconcile, so a
  // payment that never resolves shouldn't hit the gateway every 2.5s forever —
  // 4 polls at 2.5s, then 5s, then 10s, capped at 30s. The counter resets when
  // the payment status changes (a retry starts a fresh wait); terminal states
  // still stop polling outright.
  const pollCountRef = useRef(0);
  const statusQuery = useQuery({
    ...purchaseStatusQuery(purchaseId),
    enabled: !!purchaseId,
    refetchInterval: (query) => {
      if (query.state.data?.payment_status !== 'awaiting_payment') return false;
      pollCountRef.current += 1;
      if (pollCountRef.current <= 4) return 2500;
      if (pollCountRef.current <= 8) return 5000;
      if (pollCountRef.current <= 12) return 10000;
      return 30000;
    },
  });

  const paymentStatus = statusQuery.data?.payment_status;
  useEffect(() => {
    pollCountRef.current = 0;
  }, [paymentStatus]);

  const status: PaymentStatus | undefined =
    statusQuery.data?.payment_status ?? purchase?.payment_status;
  useEffect(() => {
    if (purchase?.variant_id && isTerminalStatus(status)) {
      clearCheckoutIdempotencyKey(purchase.variant_id);
    }
  }, [purchase?.variant_id, status]);

  const onRetry = async () => {
    if (!purchaseId || retry.isPending) return;
    try {
      const res = await retry.mutateAsync(purchaseId);
      goToCheckout(res.checkout_url);
    } catch {
      /* surfaced via query invalidation */
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader leftAction={<BackButton onClick={smartBackToHome} />} />

      <div className={styles.section}>
        {(status === undefined || isAwaiting(status)) && (
          <ProcessingView onHome={() => navigate({ to: '/home', replace: true })} />
        )}
        {status === 'paid' && (
          <SuccessView
            purchase={purchase}
            purchaseId={purchaseId}
            onHome={() => navigate({ to: '/home', replace: true })}
          />
        )}
        {(status === 'payment_failed' || status === 'payment_expired') &&
          // Retry eligibility (and the "retry window expired" copy) is only meaningful
          // against a loaded PurchaseDetail — a pending/failed detail read shows a
          // neutral state or an owning-surface error instead.
          (purchase ? (
            <FailureView
              status={status}
              retrying={retry.isPending}
              onRetry={onRetry}
              canRetry={canRetryPayment(purchase)}
            />
          ) : isError ? (
            <PurchaseDetailError onRetry={() => void refetch()} />
          ) : (
            <PurchaseDetailLoading />
          ))}
      </div>
    </div>
  );
}

function isTerminalStatus(s?: PaymentStatus): boolean {
  return s === 'paid' || s === 'payment_failed' || s === 'payment_expired' || s === 'refunded';
}
function isAwaiting(s?: PaymentStatus): boolean {
  return s === 'awaiting_payment';
}

function ProcessingView({ onHome }: { onHome: () => void }) {
  // UIUX-12: a delayed HitPay webhook (or a gateway abandon) leaves this page
  // polling forever; after 30s say so and offer the safe exit that SuccessView
  // already has. role="status" announces the transition either way.
  const [isSlow, setIsSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setIsSlow(true), 30_000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={styles.center} style={{ marginTop: 40 }} role="status">
      <div className={styles.spinner} />
      <p className={styles.title} style={{ marginTop: 16 }}>
        Processing payment…
      </p>
      <p className={styles.muted}>
        {isSlow
          ? "This is taking longer than usual. You don't need to keep this page open - your purchase will appear under Profile once payment is confirmed."
          : "We're confirming with HitPay. This usually takes a few seconds."}
      </p>
      {isSlow && (
        <button onClick={onHome} className={styles.secondaryBtn} style={{ marginTop: 16 }}>
          Go Home
        </button>
      )}
    </div>
  );
}

function SuccessView({
  purchase,
  purchaseId,
  onHome,
}: {
  purchase?: PurchaseDetail;
  purchaseId: string;
  onHome: () => void;
}) {
  const fulfillment = purchase?.fulfillment_status
    ? labelFulfillment(purchase.fulfillment_status)
    : null;
  return (
    <div className={styles.center} style={{ marginTop: 40 }}>
      <div className={styles.icon}>✅</div>
      <h2 className={styles.title} style={{ margin: '12px 0' }}>
        Payment successful
      </h2>
      <p className={styles.muted}>
        {fulfillment
          ? `Fulfillment: ${fulfillment}`
          : 'Your package is being prepared. We’ll credit your classes shortly.'}
      </p>
      <button onClick={onHome} className={styles.primaryBtn} style={{ marginTop: 16 }}>
        Go Home
      </button>
      {/* Real <Link> (not button+navigate): the target chunk warms while this
          page is on screen, and the detail data is already hot (this page's
          loader + usePurchase share the cache). .center is a plain text-align
          block, so inline-block reproduces the button's box the anchor's
          global reset doesn't cover. */}
      <Link
        to="/profile/purchases/$purchaseId"
        params={{ purchaseId }}
        preload="viewport"
        preloadDelay={150}
        className={styles.secondaryBtn}
        style={{ display: 'inline-block' }}
      >
        View Purchase
      </Link>
    </div>
  );
}

function FailureView({
  status,
  retrying,
  onRetry,
  canRetry,
}: {
  status: PaymentStatus;
  retrying: boolean;
  onRetry: () => void;
  canRetry: boolean;
}) {
  return (
    <div className={styles.center} style={{ marginTop: 40 }}>
      <div className={styles.icon}>⚠️</div>
      <h2 className={styles.title} style={{ margin: '12px 0' }}>
        {status === 'payment_expired' ? 'Payment expired' : 'Payment failed'}
      </h2>
      {canRetry ? (
        <>
          <p className={styles.muted}>You can try again.</p>
          <button
            onClick={onRetry}
            disabled={retrying}
            className={styles.primaryBtn}
            style={{ marginTop: 16 }}
          >
            {retrying ? 'Redirecting…' : 'Retry Payment'}
          </button>
        </>
      ) : (
        <p className={styles.muted}>The retry window for this purchase has expired.</p>
      )}
    </div>
  );
}

const labelFulfillment = (s: string) =>
  (
    ({
      sent_to_studio: 'Sent to studio',
      studio_processing: 'Studio processing',
      class_credited: 'Classes credited',
      fulfillment_failed: 'Fulfillment issue',
    }) as Record<string, string>
  )[s] ?? s;

export const Route = createFileRoute('/_authenticated/purchase/$purchaseId')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The page is a back header over a CENTERED status view (processing/success/
  // failure) — not a media-hero detail.
  pendingComponent: PurchaseStatusTemplatePending,
  loader: async ({ context, params, preload }) => {
    const statusQuery = purchaseStatusQuery(params.purchaseId);
    // Preload short-circuit: this loader's data is void, so a preload run
    // (fired by <Link preload="viewport"> on cards) just returns without
    // touching the network — there is no promise to park. Real entry re-runs
    // the loader (router defaultPreloadStaleTime is 0), which then hits the
    // cache-first gate below.
    if (preload) return;
    // Non-blocking secondary prime: the purchase DETAIL (['market','purchase',
    // id] — the same entry usePurchase reads) feeds only FailureView's retry
    // eligibility and SuccessView's fulfillment line; the page's primary gate
    // is the payment STATUS seeded below. Firing it here lets the detail land
    // during the status wait instead of one full round trip after paint (it
    // is a no-op when cached and single-flight-deduped with the mounted
    // observer), and its failure never surfaces — the page owns that UX.
    void context.queryClient
      .ensureQueryData({
        queryKey: ['market', 'purchase', params.purchaseId],
        queryFn: () =>
          authenticatedGetShared<PurchaseDetail>(`/v1/market/purchases/${params.purchaseId}`),
      })
      .catch(() => undefined);
    // Cache-first on a cold cache too: `stream()` starts the fetch without
    // being awaited, so the navigation commits straight to the page's own
    // Processing state instead of waiting out a full round trip on a pending
    // template. The SSR seed this used to protect is gone with `ssr: false`
    // on the authenticated subtree, and the mounted polling query shares this
    // exact cache entry, so it renders the first response as soon as it lands.
    const { cached, stream } = cacheFirst(context.queryClient, statusQuery.queryKey, () =>
      context.queryClient.ensureQueryData(statusQuery)
    );
    if (cached !== undefined) return;
    void stream().catch(() => undefined);
  },
  component: PurchasePage,
});
